import { z } from 'zod';

import { refineSessionTrainerFees, SessionTrainerFeeDto } from './trainer-fee';

// Booking / Scheduler (MV2-394). Literal unions rather than imports from
// ../enums, matching every other DTO file here.

const CURRENCY = z.enum(['USD', 'EUR', 'RSD', 'BAM']);

const SERIES_SCOPE = z.enum(['THIS', 'THIS_AND_FOLLOWING']);

const LOCAL_DATE = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'VALIDATION__InvalidDate');

const TIME_STRING = z
  .string()
  .regex(/^\d{2}:\d{2}$/, 'VALIDATION__InvalidTime');

const TRAINING_LEVEL = z.enum([
  'NOT_FIT',
  'ALMOST_FIT',
  'FIT',
  'PRO_FIT',
  'ADVANCED_FIT',
]);

// CUSTOM-mode dates accept either a bare local date (time-of-day comes from
// the rule's own startsAt, same as always) or an object overriding just that
// date's start time — how the assign-to-schedule flow lets someone change the
// time for only the occurrences a conflict warning flagged.
const DATE_OR_OVERRIDE = z.union([
  LOCAL_DATE,
  z.object({ date: LOCAL_DATE, startTime: TIME_STRING.optional() }).strict(),
]);

// Hard ceiling on how many occurrences one create call may materialize. Every
// occurrence is a real row, so this bounds both the write and everything that
// later reads the series.
export const BOOKING_MAX_OCCURRENCES = 400;

export const BookingRecurrenceRuleDto = z
  .object({
    freq: z.enum(['DAILY', 'WEEKLY', 'MONTHLY', 'CUSTOM']),
    // every N days/weeks/months; ignored for CUSTOM
    interval: z.number().int().positive().max(52).optional(),
    // 0=Sunday..6=Saturday, WEEKLY only
    byWeekday: z.array(z.number().int().min(0).max(6)).min(1).max(7).optional(),
    // inclusive, in the event's timezone
    untilDate: LOCAL_DATE.optional(),
    // number of DAYS the pattern runs for, not of generated slots — with
    // timesPerDay each day yields several
    count: z.number().int().positive().max(BOOKING_MAX_OCCURRENCES).optional(),
    // CUSTOM only: explicit local dates, time-of-day comes from startsAt
    // unless a date carries its own override (see DATE_OR_OVERRIDE)
    dates: z
      .array(DATE_OR_OVERRIDE)
      .min(1)
      .max(BOOKING_MAX_OCCURRENCES)
      .optional(),
    // intra-day replication ("X times a day, every N minutes")
    timesPerDay: z.number().int().min(1).max(24).optional(),
    timesPerDayGapMinutes: z
      .number()
      .int()
      .positive()
      .max(24 * 60)
      .optional(),
    // Alternative to the fixed-gap replication above: explicit start times
    // for each intra-day repeat ("again at 11:00, 16:00, 20:00"), used by the
    // template-assignment flow where the gap between repeats need not be
    // uniform. Mutually exclusive with timesPerDayGapMinutes.
    dailyStartTimes: z.array(TIME_STRING).min(1).max(24).optional(),
  })
  .strict()
  .superRefine((rule, ctx) => {
    if (rule.freq === 'CUSTOM') {
      if (!rule.dates?.length) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['dates'],
          message: 'VALIDATION__Required',
        });
      }
      return;
    }
    // A computed pattern needs exactly one end condition — neither means an
    // unbounded series, both means an ambiguous one.
    const hasUntil = Boolean(rule.untilDate);
    const hasCount = rule.count !== undefined;
    if (hasUntil === hasCount) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['untilDate'],
        message: 'VALIDATION__ExactlyOneOfUntilDateOrCount',
      });
    }
    if (rule.byWeekday && rule.freq !== 'WEEKLY') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['byWeekday'],
        message: 'VALIDATION__ByWeekdayWeeklyOnly',
      });
    }
    if (rule.dates) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['dates'],
        message: 'VALIDATION__DatesCustomOnly',
      });
    }
  })
  .superRefine((rule, ctx) => {
    if (!rule.dailyStartTimes) return;
    if (rule.timesPerDayGapMinutes !== undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['dailyStartTimes'],
        message: 'VALIDATION__DailyStartTimesExclusiveOfGap',
      });
    }
    if (
      rule.timesPerDay !== undefined &&
      rule.timesPerDay !== rule.dailyStartTimes.length
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['timesPerDay'],
        message: 'VALIDATION__TimesPerDayMustMatchDailyStartTimes',
      });
    }
  })
  .refine(
    (rule) =>
      (rule.timesPerDay ?? 1) === 1 ||
      rule.timesPerDayGapMinutes !== undefined ||
      rule.dailyStartTimes !== undefined,
    {
      path: ['timesPerDayGapMinutes'],
      message: 'VALIDATION__Required',
    },
  );

export type ApiBookingRecurrenceRuleDto = z.infer<
  typeof BookingRecurrenceRuleDto
>;

const eventFields = {
  title: z
    .string()
    .min(1, 'VALIDATION__Required')
    .max(255, 'VALIDATION__Max255CharLimit'),
  description: z.string().max(2000).nullable().optional(),
  // absolute instants; `timezone` is what the recurrence is expanded in and
  // what the event's own local day resolves against
  startsAt: z.string().datetime({ offset: true }),
  endsAt: z.string().datetime({ offset: true }),
  timezone: z.string().min(1).max(64),
  capacity: z.number().int().positive('VALIDATION__Positive'),
  vipCapacity: z.number().int().nonnegative().optional(),
  price: z
    .number()
    .nonnegative('VALIDATION__NonNegative')
    .nullable()
    .optional(),
  currency: CURRENCY.optional(),
  onlineUrl: z.string().url('VALIDATION__InvalidUrl').nullable().optional(),
  locationText: z.string().max(255).nullable().optional(),
  locationMapUrl: z
    .string()
    .url('VALIDATION__InvalidUrl')
    .nullable()
    .optional(),
  color: z.string().max(32).nullable().optional(),
  // Create-only (see CreateBookingEventDto vs UpdateBookingEventDto below) —
  // the create modal's own value when seeded from a template, frozen on the
  // event from then on, same as a template-generated occurrence's copy.
  trainingLevel: TRAINING_LEVEL.nullable().optional(),
  cancellationDeadlineHours: z.number().int().nonnegative().max(720).optional(),
  trainerUserIds: z.array(z.string().uuid()).min(1, 'VALIDATION__Required'),
  // MV2-577 — per-trainer FIXED fee in the event currency, sparse (D11):
  // omitted or `[]` changes no fee, an unlisted trainer keeps theirs, `null`
  // clears one, zero is an explicit fee. Sending it at all (even `[]`)
  // needs `trainer_fees_manage`, asserted in the service.
  trainerFees: z.array(SessionTrainerFeeDto).optional(),
  // empty = open to every client in the organization
  clientGroupIds: z.array(z.string().uuid()).optional(),
  // Narrows the audience to individual people: with groups selected these must
  // be members of them ("the whole group, or a few clients out of it"), without
  // groups they are the audience on their own. Empty = no narrowing, so the
  // gate falls back to `clientGroupIds` (and then to the whole organization).
  clientUserIds: z.array(z.string().uuid()).optional(),
  tagIds: z.array(z.string().uuid()).optional(),
  // Which organization memberships may pay for a seat on this event. Empty or
  // omitted means none may — deliberately the opposite of `clientGroupIds`'s
  // "empty = open to everyone", since a payment gate defaulting open would
  // let every membership silently cover every event.
  membershipIds: z.array(z.string().uuid()).optional(),
  // Whether this event is also listed on the marketplace as a purchasable
  // item, priced at the event's own `price`/`currency` — never touched means
  // "leave the current listing state as-is" (see UpdateBookingEventDto).
  listOnMarketplace: z.boolean().optional(),
};

export const CreateBookingEventDto = z
  .object({
    ...eventFields,
    recurrence: BookingRecurrenceRuleDto.optional(),
    // MV2-577 — the session template this create was seeded from, read ONLY
    // for its trainer fee defaults: without `trainerFees` the server copies
    // the stored defaults of the selected trainers (a trusted snapshot, like
    // the template assignment — no fee key needed); with `trainerFees` the
    // explicit input wins. Never sets the event's `templateId`.
    trainerFeeDefaultsTemplateId: z.string().uuid().optional(),
    // Trainer-overlap warnings pause the write (see BookingEventService.create)
    // the same way they do for "assign to schedule" — set only once the caller
    // has already seen the warnings and chosen to proceed anyway.
    confirmDespiteConflicts: z.boolean().optional().default(false),
  })
  .strict()
  .refine((dto) => new Date(dto.endsAt) > new Date(dto.startsAt), {
    path: ['endsAt'],
    message: 'VALIDATION__EndBeforeStart',
  })
  // VIP seats are a subset of capacity, never an addition to it
  .refine((dto) => (dto.vipCapacity ?? 0) <= dto.capacity, {
    path: ['vipCapacity'],
    message: 'VALIDATION__VipCapacityExceedsCapacity',
  })
  .superRefine(refineSessionTrainerFees);

export type ApiCreateBookingEventDto = z.infer<typeof CreateBookingEventDto>;

// The recurrence rule itself is immutable: a scope edit changes the
// occurrences' own fields, and changing the pattern means ending this series
// and creating a new one. Editing the rule in place would have to silently
// delete occurrences people may already have booked.
export const UpdateBookingEventDto = z
  .object({
    title: eventFields.title.optional(),
    description: eventFields.description,
    startsAt: eventFields.startsAt.optional(),
    endsAt: eventFields.endsAt.optional(),
    timezone: eventFields.timezone.optional(),
    capacity: eventFields.capacity.optional(),
    vipCapacity: eventFields.vipCapacity,
    price: eventFields.price,
    currency: eventFields.currency,
    onlineUrl: eventFields.onlineUrl,
    locationText: eventFields.locationText,
    locationMapUrl: eventFields.locationMapUrl,
    color: eventFields.color,
    // deliberately absent: `trainingLevel` is a create-time-only value, frozen
    // on the event for good the moment it exists — see eventFields.trainingLevel
    cancellationDeadlineHours: eventFields.cancellationDeadlineHours,
    trainerUserIds: eventFields.trainerUserIds.optional(),
    trainerFees: eventFields.trainerFees,
    clientGroupIds: eventFields.clientGroupIds,
    clientUserIds: eventFields.clientUserIds,
    tagIds: eventFields.tagIds,
    membershipIds: eventFields.membershipIds,
    listOnMarketplace: eventFields.listOnMarketplace,
    // required only for an occurrence that belongs to a series; the FE asks
    // the same "only this / this and all following" question it asks on delete
    scope: SERIES_SCOPE.optional(),
  })
  .strict()
  .refine(
    (dto) =>
      dto.startsAt === undefined ||
      dto.endsAt === undefined ||
      new Date(dto.endsAt) > new Date(dto.startsAt),
    { path: ['endsAt'], message: 'VALIDATION__EndBeforeStart' },
  )
  .superRefine(refineSessionTrainerFees);

export type ApiUpdateBookingEventDto = z.infer<typeof UpdateBookingEventDto>;

export const DeleteBookingEventDto = z
  .object({
    scope: SERIES_SCOPE.optional(),
    // optional note appended to the cancellation notice sent to everyone
    // holding a seat
    message: z.string().max(1000).optional(),
  })
  .strict()
  // A DELETE may legitimately carry no body at all; without the default the
  // validation pipe would reject the request before it reaches the handler.
  .default({});

export type ApiDeleteBookingEventDto = z.infer<typeof DeleteBookingEventDto>;

export const ListBookingEventsQueryDto = z
  .object({
    from: z.string().datetime({ offset: true }).optional(),
    to: z.string().datetime({ offset: true }).optional(),
    trainerUserId: z.string().uuid().optional(),
    // narrows to events where this client holds an active seat — a single
    // client's calendar must show only what they're actually booked into,
    // not every bookable slot in the organization
    clientUserId: z.string().uuid().optional(),
    // cancelled events stay in the table for the statistics, so they are
    // excluded unless explicitly asked for
    includeCancelled: z.coerce.boolean().optional(),
  })
  .strict();

export type ApiListBookingEventsQueryDto = z.infer<
  typeof ListBookingEventsQueryDto
>;

// ── Client-side booking ─────────────────────────────────────────────────────

// One call books several slots (the "select several, book all" flow). Each is
// taken independently, so a full or ineligible slot never discards the rest.
export const CreateBookingsDto = z
  .object({
    bookingEventIds: z.array(z.string().uuid()).min(1).max(50),
    // the event's single shared privileged-seat link; seats then come out of
    // the VIP pool and the client-group restriction does not apply
    vipToken: z.string().min(1).max(128).optional(),
    // Only consulted when no package of the client's covers the event.
    // ONSITE ("pay at the desk") is not a self-service choice — it is only
    // ever set by staff booking a walk-in on the client's behalf (see
    // `clientUserId` below); a client's own booking call must send ONLINE
    // (card on file, charged only if not cancelled by the deadline) or omit
    // this entirely for a free/membership-only event. While card payment for
    // seats is switched off (BOOKING_SEAT_CARD_PAYMENT_ENABLED, the event
    // listing's `onsitePaymentOnly`), the client omits it for a paid seat
    // too: the server books it to be paid at the desk and refuses ONLINE.
    paymentMethod: z.enum(['ONSITE', 'ONLINE']).optional(),
    /**
     * Which of the client's packages to draw the session from. Omitted means
     * "decide for me" and keeps the soonest-expiring rule; given, it is honoured
     * or the slot is rejected — never quietly swapped for another package, since
     * which one is spent is exactly what the client was asked.
     *
     * Applies to every requested slot, so a multi-select spanning two packages'
     * date ranges must be sent as separate calls.
     */
    userMembershipId: z.string().uuid().optional(),
    // Staff-only: book a walk-in on this client's behalf instead of the
    // caller's own account. Requires the caller to hold `bookings_manage`;
    // forces ONSITE regardless of `paymentMethod` — a walk-in is by
    // definition someone who did not book (and pay) online in advance.
    clientUserId: z.string().uuid().optional(),
    // Task 5 (membership-renewal plan) — the used-up-choices dialog's "book
    // without membership (pay on site)" option: skips membership payment
    // resolution entirely and books ONSITE with `userMembershipId: null`.
    // Staff-only, same as `clientUserId` above (the controller's existing
    // `bookings_manage` check already covers it, since this is refused
    // without `clientUserId`).
    ignoreMemberships: z.boolean().optional(),
    // Staff-only: seat a walk-in who has NO account (a Prospect). Exclusive
    // with `clientUserId`; always ONSITE, never draws on a package, and takes
    // a regular seat like any other booking, so it counts toward capacity.
    prospectId: z.string().uuid().optional(),
    // "Probni trening" marker for a prospect walk-in. A trial visit is free:
    // the seat is recorded with no payment owed.
    isTrial: z.boolean().optional(),
  })
  .strict()
  .refine(
    (dto) =>
      dto.paymentMethod !== 'ONSITE' ||
      dto.clientUserId !== undefined ||
      dto.prospectId !== undefined,
    {
      path: ['paymentMethod'],
      message: 'VALIDATION__OnsitePaymentStaffOnly',
    },
  )
  .refine(
    (dto) => dto.prospectId === undefined || dto.clientUserId === undefined,
    {
      path: ['prospectId'],
      message: 'VALIDATION__ProspectExcludesClientUserId',
    },
  )
  .refine(
    (dto) =>
      dto.prospectId === undefined ||
      (dto.userMembershipId === undefined && !dto.vipToken),
    {
      path: ['prospectId'],
      message: 'VALIDATION__ProspectExcludesMembershipAndVip',
    },
  )
  .refine((dto) => !dto.isTrial || dto.prospectId !== undefined, {
    path: ['isTrial'],
    message: 'VALIDATION__TrialRequiresProspect',
  })
  .refine((dto) => !dto.ignoreMemberships || dto.clientUserId !== undefined, {
    path: ['ignoreMemberships'],
    message: 'VALIDATION__IgnoreMembershipsStaffOnly',
  })
  // `ignoreMemberships` skips package resolution outright — a `userMembershipId`
  // alongside it would be a caller belief ("draw from THIS package") the
  // server silently ignores instead of a 400 saying so.
  .refine(
    (dto) => !dto.ignoreMemberships || dto.userMembershipId === undefined,
    {
      path: ['userMembershipId'],
      message: 'VALIDATION__IgnoreMembershipsExcludesUserMembershipId',
    },
  );

export type ApiCreateBookingsDto = z.infer<typeof CreateBookingsDto>;

// Attendance, not consumption: the session was already taken when the seat
// was. A no-show is recorded, never refunded.
export const MarkBookingAttendanceDto = z
  .object({
    status: z.enum(['ATTENDED', 'NO_SHOW']),
    notes: z.string().max(2000).optional(),
  })
  .strict();

export type ApiMarkBookingAttendanceDto = z.infer<
  typeof MarkBookingAttendanceDto
>;

// Staff-only: record that a PENDING_ONSITE booking (a walk-in) was settled at
// the front desk. Not available for anything else — an online booking is
// either covered by a package, or on its own card-hold/capture path; there is
// no "convert to pay-at-desk" escape hatch once a client has booked online.
export const MarkBookingPaidDto = z
  .object({
    method: z.enum(['CASH', 'CARD', 'TRANSFER', 'OTHER']),
    notes: z.string().max(2000).optional(),
  })
  .strict();

export type ApiMarkBookingPaidDto = z.infer<typeof MarkBookingPaidDto>;

export const ListBookingsQueryDto = z
  .object({
    from: z.string().datetime({ offset: true }).optional(),
    to: z.string().datetime({ offset: true }).optional(),
    // staff only — a client always sees their own bookings and nobody else's
    clientUserId: z.string().uuid().optional(),
    bookingEventId: z.string().uuid().optional(),
    includeCancelled: z.coerce.boolean().optional(),
  })
  .strict();

export type ApiListBookingsQueryDto = z.infer<typeof ListBookingsQueryDto>;

// The statistics period. Both ends required — an unbounded aggregate over every
// event the organization has ever run is never the question being asked, and
// would only get slower.
export const BookingStatsQueryDto = z
  .object({
    from: z.string().datetime({ offset: true }),
    to: z.string().datetime({ offset: true }),
  })
  .strict()
  .refine((dto) => new Date(dto.to) > new Date(dto.from), {
    path: ['to'],
    message: 'VALIDATION__EndBeforeStart',
  });

export type ApiBookingStatsQueryDto = z.infer<typeof BookingStatsQueryDto>;

// Queueing for full slots. Multi-select like booking: a client picking several
// full slots should not have to send one request per slot.
export const JoinBookingWaitlistDto = z
  .object({
    bookingEventIds: z.array(z.string().uuid()).min(1).max(50),
    // a privileged-seat link holder may queue too — the VIP pool can fill up
    vipToken: z.string().min(1).max(128).optional(),
  })
  .strict();

export type ApiJoinBookingWaitlistDto = z.infer<typeof JoinBookingWaitlistDto>;

// The slots a client may book. `vipToken` switches the view: with it the caller
// also sees the privileged pool and the events the group restriction would
// otherwise hide from them.
export const AvailableBookingEventsQueryDto = z
  .object({
    from: z.string().datetime({ offset: true }).optional(),
    to: z.string().datetime({ offset: true }).optional(),
    vipToken: z.string().min(1).max(128).optional(),
  })
  .strict();

export type ApiAvailableBookingEventsQueryDto = z.infer<
  typeof AvailableBookingEventsQueryDto
>;

// Upcoming instances of one recurring class — the picker behind "book
// multiple sessions at once". Bounded by `limit` since a series can run
// indefinitely.
export const BookingSeriesOccurrencesQueryDto = z
  .object({
    from: z.string().datetime({ offset: true }).optional(),
    limit: z.coerce.number().int().min(1).max(60).optional(),
  })
  .strict();

export type ApiBookingSeriesOccurrencesQueryDto = z.infer<
  typeof BookingSeriesOccurrencesQueryDto
>;

// ── Booking event templates ─────────────────────────────────────────────────
// A reusable stencil: same fields as a BookingEvent minus anything date-shaped
// (no startsAt/endsAt/timezone — those only exist once it is assigned to the
// schedule), plus a duration, a training level, an optional cover image, and
// the membership tagging that also lives on the event itself.

const templateEventFields = {
  title: eventFields.title,
  description: eventFields.description,
  durationMinutes: z.number().int().positive('VALIDATION__Positive'),
  trainingLevel: TRAINING_LEVEL.nullable().optional(),
  coverImageUrl: z.string().nullable().optional(),
  capacity: eventFields.capacity,
  vipCapacity: eventFields.vipCapacity,
  price: eventFields.price,
  currency: eventFields.currency,
  onlineUrl: eventFields.onlineUrl,
  locationText: eventFields.locationText,
  locationMapUrl: eventFields.locationMapUrl,
  color: eventFields.color,
  cancellationDeadlineHours: eventFields.cancellationDeadlineHours,
  // the pool of trainers who may run a session generated from this template
  trainerUserIds: eventFields.trainerUserIds,
  // the fee each trainer starts with on an event generated from this
  // template; same sparse semantics and permission as on the event
  trainerFees: eventFields.trainerFees,
  clientGroupIds: eventFields.clientGroupIds,
  clientUserIds: eventFields.clientUserIds,
  tagIds: eventFields.tagIds,
  membershipIds: eventFields.membershipIds,
};

// A non-binding prefill for the "assign to schedule" flow — nothing here is
// validated against BookingRecurrenceRuleDto, it just seeds the assign form's
// defaults; recurrence itself is only ever decided at assignment time.
const RecurrenceDefaultsDto = z
  .object({
    mode: z.enum(['SINGLE', 'RECURRING']).optional(),
    recurrenceMode: z.enum(['PATTERN', 'CUSTOM']).optional(),
    byWeekday: z.array(z.number().int().min(0).max(6)).optional(),
    endDateOption: z.enum(['DATE', '3M', '6M', '12M']).optional(),
    dailyRepeatsCount: z.number().int().min(1).max(24).optional(),
  })
  .strict();

export const CreateBookingEventTemplateDto = z
  .object({
    ...templateEventFields,
    recurrenceDefaults: RecurrenceDefaultsDto.optional(),
  })
  .strict()
  // VIP seats are a subset of capacity, never an addition to it
  .refine((dto) => (dto.vipCapacity ?? 0) <= dto.capacity, {
    path: ['vipCapacity'],
    message: 'VALIDATION__VipCapacityExceedsCapacity',
  })
  .superRefine(refineSessionTrainerFees);

export type ApiCreateBookingEventTemplateDto = z.infer<
  typeof CreateBookingEventTemplateDto
>;

export const UpdateBookingEventTemplateDto = z
  .object({
    title: templateEventFields.title.optional(),
    description: templateEventFields.description,
    durationMinutes: templateEventFields.durationMinutes.optional(),
    trainingLevel: templateEventFields.trainingLevel,
    coverImageUrl: templateEventFields.coverImageUrl,
    capacity: templateEventFields.capacity.optional(),
    vipCapacity: templateEventFields.vipCapacity,
    price: templateEventFields.price,
    currency: templateEventFields.currency,
    onlineUrl: templateEventFields.onlineUrl,
    locationText: templateEventFields.locationText,
    locationMapUrl: templateEventFields.locationMapUrl,
    color: templateEventFields.color,
    cancellationDeadlineHours: templateEventFields.cancellationDeadlineHours,
    trainerUserIds: templateEventFields.trainerUserIds.optional(),
    trainerFees: templateEventFields.trainerFees,
    clientGroupIds: templateEventFields.clientGroupIds,
    clientUserIds: templateEventFields.clientUserIds,
    tagIds: templateEventFields.tagIds,
    membershipIds: templateEventFields.membershipIds,
    recurrenceDefaults: RecurrenceDefaultsDto.optional(),
  })
  .strict()
  .superRefine(refineSessionTrainerFees);

export type ApiUpdateBookingEventTemplateDto = z.infer<
  typeof UpdateBookingEventTemplateDto
>;

export const ListBookingEventTemplatesQueryDto = z
  .object({
    // list is always returned sorted by title ascending — nothing to pick here
    search: z.string().trim().max(255).optional(),
  })
  .strict();

export type ApiListBookingEventTemplatesQueryDto = z.infer<
  typeof ListBookingEventTemplatesQueryDto
>;

// "Assign to schedule": turns a template into one or more real BookingEvents.
// Kept separate from CreateBookingEventDto — a template has no date of its
// own, and this is the one place that decision gets made.
export const AssignBookingEventTemplateDto = z
  .object({
    timezone: z.string().min(1).max(64),
    mode: z.enum(['SINGLE', 'RECURRING']),
    // SINGLE
    date: LOCAL_DATE.optional(),
    startTime: TIME_STRING.optional(),
    // RECURRING — reuses the same rule the legacy event modal builds; Pattern
    // maps to freq:'WEEKLY'+byWeekday, Custom to freq:'CUSTOM'+dates
    recurrence: BookingRecurrenceRuleDto.optional(),
    // Trainer-overlap warnings never block a legacy BookingEvent create, but
    // this flow pauses on them — set only once the caller has already seen
    // the warnings and chosen to proceed anyway.
    confirmDespiteConflicts: z.boolean().optional().default(false),
    // Which of the template's own trainers actually run this occurrence —
    // omitted means "all of them", same as before this field existed. When
    // sent, the service re-validates it against the template's current
    // trainer pool rather than trusting it outright.
    trainerUserIds: z.array(z.string()).optional(),
  })
  .strict()
  .superRefine((dto, ctx) => {
    if (dto.mode === 'SINGLE') {
      if (!dto.date) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['date'],
          message: 'VALIDATION__Required',
        });
      }
      if (!dto.startTime) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['startTime'],
          message: 'VALIDATION__Required',
        });
      }
    } else if (!dto.recurrence) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['recurrence'],
        message: 'VALIDATION__Required',
      });
    }
  });

export type ApiAssignBookingEventTemplateDto = z.infer<
  typeof AssignBookingEventTemplateDto
>;

/** A stable cursor independent of changing refund state or calendar dates. */
export const BookingRefundHistoryQueryDto = z.object({
  cursor: z.string().uuid().optional(),
  limit: z.coerce.number().int().min(1).max(50).default(20),
  organizationId: z.string().uuid().optional(),
});
export type ApiBookingRefundHistoryQueryDto = z.infer<
  typeof BookingRefundHistoryQueryDto
>;
